import React from 'react';
import { render, screen, fireEvent, waitFor } from '@testing-library/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import ProfileTab, { formatAddress } from '../ProfileTab';
import Api from '../API';
import { InitialCandidateState } from '../reducer';

jest.mock('../API');
const mockApi = Api as jest.Mocked<typeof Api>;

jest.mock('../../Components/GenericTable/GenericTable', () => {
  return function MockGenericTable({
    items,
    isLoadingItems
  }: {
    items: any[];
    isLoadingItems: boolean;
  }) {
    return (
      <div data-testid="generic-table">
        <div data-testid="loading">{isLoadingItems ? 'Loading...' : 'Loaded'}</div>
        <div data-testid="items-count">{items?.length || 0}</div>
        {items?.map((item: any) => (
          <div key={item.id} data-testid={`application-${item.id}`}>
            {item.job_title}
          </div>
        ))}
      </div>
    );
  };
});

jest.mock('../CandidateTags', () => {
  return function MockCandidateTags({ candidate }: { candidate: any }) {
    return <div data-testid="candidate-tags">Tags for {candidate.firstname}</div>;
  };
});

jest.mock('../Notepad', () => {
  return function MockNotepad({
    note,
    updateNote
  }: {
    note?: string;
    updateNote: (note: string) => void;
  }) {
    return (
      <div data-testid="notepad">
        <textarea
          data-testid="notepad-input"
          value={note || ''}
          onChange={(e) => updateNote(e.target.value)}
        />
      </div>
    );
  };
});

jest.mock('../CandidateTasks', () => {
  return function MockCandidateTasks({ candidate }: { candidate: any }) {
    return <div data-testid="candidate-tasks">Tasks for {candidate.firstname}</div>;
  };
});

jest.mock('../CandidateIcon', () => {
  return function MockCandidateIcon({ existingCandidate }: { existingCandidate: boolean }) {
    return <div data-testid="candidate-icon">{existingCandidate ? 'Existing' : 'New'}</div>;
  };
});

const createTestQueryClient = () =>
  new QueryClient({
    defaultOptions: {
      queries: {
        retry: false
      }
    }
  });

const theme = createTheme();

const renderWithProviders = (component: React.ReactElement) => {
  const queryClient = createTestQueryClient();
  return render(
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>{component}</ThemeProvider>
    </QueryClientProvider>
  );
};

const mockCandidate = {
  id: 1,
  firstname: 'John',
  lastname: 'Doe',
  email: 'john.doe@example.com',
  phone1: '1234567890',
  phone2: '0987654321',
  title: 'Mr',
  gender: 'm',
  employee_id: 'EMP001',
  linkedin_url: 'https://linkedin.com/in/johndoe',
  existing_candidate: true,
  address_details: {
    id: 1,
    street_number: '123',
    street1: 'Main St',
    street2: 'Apt 4B',
    street_type: 'Street',
    suburb: 'Test City',
    postcode: '12345',
    state: { id: 1, name: 'NSW' },
    country: { id: 1, name: 'Australia', printable_name: 'Australia', iso: 'AU' }
  },
  tag_list: ['tag1', 'tag2'],
  entity_tags: [{ value: 'tag1', label: 'Tag 1' }],
  notepad: { body: 'Test note' }
};

const mockApplications = {
  res: {
    applications: [
      {
        id: 1,
        job_title: 'Software Engineer',
        application_link: '/applications/1',
        entity: 'Test Entity',
        entity_link: '/entities/1',
        created_at: '2023-01-01T00:00:00Z',
        rating: '4.5',
        status: 'Active',
        status_colour: '#00FF00',
        sister_entity_application: false,
        attachments: []
      }
    ]
  },
  resHead: {
    'x-total-count': '1',
    'x-total-pages': '1'
  }
};

describe('ProfileTab', () => {
  const mockDispatch = jest.fn();

  beforeEach(() => {
    jest.clearAllMocks();
    mockApi.getCandidateApplications.mockResolvedValue(mockApplications);
    mockApi.updateCandidateNotepad.mockResolvedValue({ res: { success: 'Notepad updated' } });
  });

  it('renders candidate information correctly', async () => {
    renderWithProviders(
      <ProfileTab
        candidate={mockCandidate}
        CandidateState={InitialCandidateState}
        dispatch={mockDispatch}
      />
    );

    await waitFor(() => {
      expect(screen.getByText('John Doe')).toBeInTheDocument();
      expect(screen.getByText('john.doe@example.com')).toBeInTheDocument();
      expect(screen.getByText('1234567890')).toBeInTheDocument();
      expect(screen.getByText('0987654321')).toBeInTheDocument();
      expect(screen.getByText('EMPLOYEE ID: EMP001')).toBeInTheDocument();
    });
  });

  it('renders LinkedIn button when linkedin_url is provided', async () => {
    const windowOpenSpy = jest.spyOn(window, 'open').mockImplementation(() => null);

    renderWithProviders(
      <ProfileTab
        candidate={mockCandidate}
        CandidateState={InitialCandidateState}
        dispatch={mockDispatch}
      />
    );

    await waitFor(() => {
      const linkedInButton = screen.getByRole('button');
      expect(linkedInButton).toBeInTheDocument();
      fireEvent.click(linkedInButton);
      expect(windowOpenSpy).toHaveBeenCalledWith('https://linkedin.com/in/johndoe', '_blank');
    });

    windowOpenSpy.mockRestore();
  });

  it('does not render LinkedIn button when linkedin_url is not provided', async () => {
    const candidateWithoutLinkedIn = { ...mockCandidate, linkedin_url: null };

    renderWithProviders(
      <ProfileTab
        candidate={candidateWithoutLinkedIn}
        CandidateState={InitialCandidateState}
        dispatch={mockDispatch}
      />
    );

    await waitFor(() => {
      expect(screen.queryByRole('button')).not.toBeInTheDocument();
    });
  });

  it('does not render employee ID when not provided', async () => {
    const candidateWithoutEmployeeId = { ...mockCandidate, employee_id: null };

    renderWithProviders(
      <ProfileTab
        candidate={candidateWithoutEmployeeId}
        CandidateState={InitialCandidateState}
        dispatch={mockDispatch}
      />
    );

    await waitFor(() => {
      expect(screen.queryByText(/EMPLOYEE ID/)).not.toBeInTheDocument();
    });
  });

  it('does not render phone2 when not provided', async () => {
    const candidateWithoutPhone2 = { ...mockCandidate, phone2: null };

    renderWithProviders(
      <ProfileTab
        candidate={candidateWithoutPhone2}
        CandidateState={InitialCandidateState}
        dispatch={mockDispatch}
      />
    );

    await waitFor(() => {
      expect(screen.queryByText('0987654321')).not.toBeInTheDocument();
    });
  });

  it('renders applications table', async () => {
    renderWithProviders(
      <ProfileTab
        candidate={mockCandidate}
        CandidateState={InitialCandidateState}
        dispatch={mockDispatch}
      />
    );

    await waitFor(() => {
      expect(screen.getByTestId('generic-table')).toBeInTheDocument();
      expect(screen.getByText('Applications')).toBeInTheDocument();
      expect(screen.getByText('Software Engineer')).toBeInTheDocument();
    });
  });

  it('displays loading state for applications', async () => {
    mockApi.getCandidateApplications.mockImplementation(() => new Promise(() => {}));

    renderWithProviders(
      <ProfileTab
        candidate={mockCandidate}
        CandidateState={InitialCandidateState}
        dispatch={mockDispatch}
      />
    );

    await waitFor(() => {
      expect(screen.getByText('Loading...')).toBeInTheDocument();
    });
  });

  it('renders candidate tags component', async () => {
    renderWithProviders(
      <ProfileTab
        candidate={mockCandidate}
        CandidateState={InitialCandidateState}
        dispatch={mockDispatch}
      />
    );

    await waitFor(() => {
      expect(screen.getByTestId('candidate-tags')).toBeInTheDocument();
      expect(screen.getByText('Tags for John')).toBeInTheDocument();
    });
  });

  it('renders notepad component', async () => {
    renderWithProviders(
      <ProfileTab
        candidate={mockCandidate}
        CandidateState={InitialCandidateState}
        dispatch={mockDispatch}
      />
    );

    await waitFor(() => {
      expect(screen.getByTestId('notepad')).toBeInTheDocument();
      const notepadInput = screen.getByTestId('notepad-input') as HTMLTextAreaElement;
      expect(notepadInput.value).toBe('Test note');
    });
  });

  it('renders candidate tasks component', async () => {
    renderWithProviders(
      <ProfileTab
        candidate={mockCandidate}
        CandidateState={InitialCandidateState}
        dispatch={mockDispatch}
      />
    );

    await waitFor(() => {
      expect(screen.getByTestId('candidate-tasks')).toBeInTheDocument();
      expect(screen.getByText('Tasks for John')).toBeInTheDocument();
    });
  });

  it('handles applications API error', async () => {
    const consoleError = jest.spyOn(console, 'error').mockImplementation(() => undefined);
    mockApi.getCandidateApplications.mockRejectedValue(new Error('API Error'));

    renderWithProviders(
      <ProfileTab
        candidate={mockCandidate}
        CandidateState={InitialCandidateState}
        dispatch={mockDispatch}
      />
    );

    await waitFor(() => {
      expect(mockDispatch).toHaveBeenCalledWith({
        type: 'SET_SNACKBAR',
        payload: {
          message: 'There was an error getting candidate data, Error: API Error',
          state: 'error'
        }
      });
    });

    consoleError.mockRestore();
  });

  it('calls API with correct parameters for applications', async () => {
    renderWithProviders(
      <ProfileTab
        candidate={mockCandidate}
        CandidateState={InitialCandidateState}
        dispatch={mockDispatch}
      />
    );

    await waitFor(() => {
      expect(mockApi.getCandidateApplications).toHaveBeenCalledWith(1, {
        page: 1,
        limit: 10,
        sort: 'created_at',
        sort_order: 'desc'
      });
    });
  });
});

describe('formatAddress', () => {
  it('formats address correctly with all fields', () => {
    const address = {
      id: 1,
      street_number: '123',
      street1: 'Main St',
      street2: 'Apt 4B',
      street_type: 'Street',
      suburb: 'Test City',
      postcode: '12345',
      state: { id: 1, name: 'NSW' },
      country: { id: 1, name: 'Australia', printable_name: 'Australia', iso: 'AU' }
    };

    const result = formatAddress(address);
    expect(result).toBe('123 Main St Apt 4B Street, Test City, NSW, Australia 12345');
  });

  it('formats address correctly with minimal fields', () => {
    const address = {
      id: 1,
      street_number: '123',
      street1: 'Main St',
      street2: null,
      street_type: null,
      suburb: 'Test City',
      postcode: '12345',
      state: { id: 1, name: 'NSW' },
      country: { id: 1, name: 'Australia', printable_name: 'Australia', iso: 'AU' }
    };

    const result = formatAddress(address);
    expect(result).toBe('123 Main St, Test City, NSW, Australia 12345');
  });

  it('handles missing address details', () => {
    const address = {
      id: 1,
      street_number: null,
      street1: null,
      street2: null,
      street_type: null,
      suburb: null,
      postcode: null,
      state: null,
      country: null
    };

    const result = formatAddress(address);
    expect(result).toBe('');
  });

  it('handles partial address details', () => {
    const address = {
      id: 1,
      street_number: '123',
      street1: 'Main St',
      street2: null,
      street_type: null,
      suburb: 'Test City',
      postcode: null,
      state: null,
      country: { id: 1, name: 'Australia', printable_name: 'Australia', iso: 'AU' }
    };

    const result = formatAddress(address);
    expect(result).toBe('123 Main St, Test City, Australia');
  });
});
